import React, { useEffect, useMemo } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { BasisProvider } from 'basis';
import { useSiteTracking } from 'react-event-tracker';
import { HelmetProvider } from 'react-helmet-async';
import { trackingConfig } from '_config';
import { ContextProvider } from 'context';
import { DevFeatureToggle } from '__dev';
import { useDatadog, useTealium } from 'hooks';
import { ErrorBoundary, Metadata } from 'components';
import { theme } from 'theme';
import { Router } from './Router';
import { getSessionStorage, inferSource, isWithStartingLetter } from 'utils';
import { updateDataDogContext } from './hooks/useDatadog';
import { CountryCode, State } from 'types';
import { match, P } from 'ts-pattern';
import { QueryClientProvider } from '@tanstack/react-query';
import { reactQuery } from 'libs/reactQuery';

const SOURCE_KEY = 'source';
const ASSESSMENT_ID_KEY = 'assessmentId';
const CORRELATION_ID_KEY = 'correlationId';
const EMPLOYMENT_STATUS_KEY = 'empStatus';
const APP_NUMBER_KEY = 'appNum';
const CARD_TYPE_KEY = 'cardType';
const CHANNEL_KEY = 'channel';
const OPERATION_KEY = 'operation';

const App: React.FC = () => {
  const url = new URL(window.location.href);
  const appNumInput = getParam(url, APP_NUMBER_KEY)?.toLowerCase();
  const sourceInput = getParam(url, SOURCE_KEY)?.toLowerCase() as State['source'] | undefined;
  const operation = getParam(url, OPERATION_KEY)?.toLowerCase() as State['operation'] | undefined;
  const assessmentId = getParam(url, ASSESSMENT_ID_KEY);
  const correlationId = getParam(url, CORRELATION_ID_KEY);
  const empStatus = getParam(url, EMPLOYMENT_STATUS_KEY);
  const cardType = getParam(url, CARD_TYPE_KEY);
  const channel = getParam(url, CHANNEL_KEY)?.toLowerCase();
  const dev = getParam(url, 'dev') === 'true';
  const source = operation
    ? undefined
    : sourceInput || inferSource({ appNum: appNumInput, hostname: window.location.hostname });

  const { flow, countryCode, }: { flow: State['flow'], countryCode: State['countryCode'] } =
    match({ operation, source, assessmentId, correlationId })
      .with({ assessmentId: undefined, correlationId: P.string }, () => ({ flow: 'acceptance', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'website-au' }, () => ({ flow: 'upload-documents', countryCode: CountryCode.AU }) as const)
      .with({ source: 'website-nz' }, () => ({ flow: 'upload-documents', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'smp-au' }, () => ({ flow: 'proof-of-income', countryCode: CountryCode.AU }) as const)
      .with({ source: 'smp-nz' }, () => ({ flow: 'proof-of-income', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'cli-au' }, () => ({ flow: 'proof-of-income', countryCode: CountryCode.AU }) as const)
      .with({ source: 'cli-nz' }, () => ({ flow: 'proof-of-income', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'apply-au' }, () => ({ flow: 'proof-of-income', countryCode: CountryCode.AU }) as const)
      .with({ source: 'apply-nz' }, () => ({ flow: 'proof-of-income', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'ach-nz' }, () => ({ flow: 'additional-card-holder-upload-documents', source: 'ach-nz', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'smp-aml-nz' }, () => ({ flow: 'aml-nz', source: 'smp-aml-nz', countryCode: CountryCode.NZ }) as const)
      .with({ source: 'assisted-apply-au' }, () => ({ flow: 'assisted-apply', countryCode: CountryCode.AU }) as const)
      .with({ source: 'assisted-apply-nz' }, () => ({ flow: 'assisted-apply', countryCode: CountryCode.NZ }) as const)
      .with({ operation: 'ach-au' }, () => ({ flow: 'additional-card-holder-identity-verification', countryCode: CountryCode.AU }) as const)
      .with({ operation: 'ach-nz' }, () => ({ flow: 'additional-card-holder-identity-verification', countryCode: CountryCode.NZ }) as const)
      .with({ operation: 'ach-nz' }, () => ({ flow: 'additional-card-holder-upload-documents', source: 'ach-nz', countryCode: CountryCode.NZ }) as const)
      .with({ operation: undefined, source: undefined, correlationId: undefined }, () => {
        throw new Error('At least one of source, correlationId or operation is required');
      })
      .otherwise(() => {
        throw new Error(`Unknown flow - operation: ${operation}; source: ${source}`);
      });

  const queryParams = useMemo(
    () => ({
      source,
      operation,
      assessmentId,
      correlationId,
      empStatus,
      cardType,
      channel,
      flow,
      countryCode,
      dev,
      /**
       * If the appNum starts with letter like 'f' or 's', it's from SMP AU QR code linked to the legacy NTT doc uploader.
       * See the full details at https://l-fs.slack.com/archives/C07F0B7VABY/p1733794830709149?thread_ts=1733721611.146089&cid=C07F0B7VABY
       */
      appNum: isWithStartingLetter(appNumInput) ? (appNumInput as string).slice(1) : appNumInput,
    }),
    [source, operation, assessmentId, correlationId, empStatus, appNumInput, cardType, channel, flow, countryCode, dev],
  );

  useEffect(() => {
    updateDataDogContext(queryParams);
  }, [queryParams]);

  useDatadog();
  useTealium();

  const { SiteTracking } = useSiteTracking(trackingConfig);

  return (
    <HelmetProvider>
      <Metadata />
      <SiteTracking>
        <BasisProvider theme={theme}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={reactQuery}>
              <ContextProvider overrideInitState={queryParams}>
                <ErrorBoundary>
                  <Router />
                  <DevFeatureToggle />
                </ErrorBoundary>
              </ContextProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </BasisProvider>
      </SiteTracking>
    </HelmetProvider>
  );
};

export default App;

function getParam<T extends string | undefined = undefined>(
  url: URL,
  key: string,
  defaultValue?: T,
): T extends string ? string : string | undefined {
  return (url.searchParams.get(key) ||
    url.searchParams.get(key.toLowerCase()) ||
    getSessionStorage('state', key) ||
    defaultValue) as T extends string ? string : string | undefined;
}
